/**
 * @fileOverview The walkthrough's keyboard: one map from keys to what the player does, the shortcut
 * list it opens, and the brief feedback a key leaves on the stage. Used by `LewViewHelpDialog`, for
 * as long as it is mounted. See docs/view-help.md for the table of keys.
 */

import { onBeforeUnmount, onMounted, ref, type Ref } from "vue"
import { useViewHelp } from "./useViewHelp"
import { useViewHelpCopy } from "./useViewHelpCopy"

/** How long a key's feedback stays on the stage. */
const FLASH_MS = 650

export interface KeyFlash {
  /** Keyed, so pressing the same key again replays the flash instead of extending it. */
  id: number
  icon?: string
  label?: string
}

export function useViewHelpKeyboard(dialog: Ref<HTMLElement | null>) {
  const player = useViewHelp()
  const { t, formatSpeed } = useViewHelpCopy()

  // ── how focus arrived ───────────────────────────────────────────────────────

  /**
   * The reader's last kind of input, and the elements that got focus from a press of the mouse rather
   * than from the keyboard.
   *
   * Recorded as focus arrives, not asked afterwards. `:focus-visible` looked like the answer and is
   * not: Chrome turns it on for a clicked button at the first key pressed on it — so at the moment of
   * the Space keydown, a timeline segment clicked a moment ago already read as reached by keyboard,
   * took the Space, and restarted its step instead of pausing.
   */
  let lastInput: "pointer" | "keyboard" = "pointer"
  const focusedByPointer = new WeakSet<EventTarget>()

  const onPointerDown = () => (lastInput = "pointer")
  const onAnyKeydown = () => (lastInput = "keyboard")
  function onFocusIn(event: FocusEvent) {
    if (!event.target) return
    if (lastInput === "pointer") focusedByPointer.add(event.target)
    else focusedByPointer.delete(event.target)
  }

  /** Whether the reader got to `el` by keyboard — Tab, or focus handed back after a key. */
  const reachedByKeyboard = (el: Element) => !focusedByPointer.has(el)

  /**
   * Whether Space pressed on `target` is that control's own rather than the player's.
   *
   * A text field always keeps it. A button keeps it only when the reader got there by keyboard: a
   * button that was *clicked* keeps focus too — browsers leave it there — and Space after a click is
   * meant for the player, as on any video player. Leaving it to the clicked button pressed that button
   * again: Space after a click on the timeline restarted the step, after closing the shortcut list it
   * reopened the list, and after a click on ✕ it would have closed the walkthrough. Enter still
   * presses whatever is focused.
   */
  function spaceBelongsTo(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false
    const control = target.closest<HTMLElement>("button, a, input, select, textarea, [role=button]")
    if (!control) return false
    return control.matches("input, select, textarea") || reachedByKeyboard(control)
  }

  /** Whether the list of keyboard shortcuts is open. */
  const shortcutsOpen = ref(false)

  /** A key's effect, shown briefly over the stage. */
  const flash = ref<KeyFlash | null>(null)

  /** The same, for a screen reader: the flash itself is decoration. */
  const announcement = ref("")

  // ── the shortcut list ───────────────────────────────────────────────────────

  /** Whether playback was running when the list opened, so closing it can pick up where it was. */
  let resumeAfterShortcuts = false
  let focusBeforeShortcuts: HTMLElement | null = null

  function openShortcuts() {
    if (shortcutsOpen.value) return
    resumeAfterShortcuts = player.playing.value
    player.setPlaying(false)
    // Only keyboard focus is worth giving back. A button that merely kept focus after a click — the
    // keyboard button itself, most often — would, once focused again, take the next Space for itself.
    const active = document.activeElement as HTMLElement | null
    focusBeforeShortcuts = active && reachedByKeyboard(active) ? active : null
    shortcutsOpen.value = true
  }

  function closeShortcuts(returnFocus = false) {
    if (!shortcutsOpen.value) return
    shortcutsOpen.value = false
    if (resumeAfterShortcuts) player.setPlaying(true)
    resumeAfterShortcuts = false
    if (returnFocus) {
      // Back where the reader was by keyboard — or else to the dialog itself, where every key is the
      // player's. Never the frame, which is deliberately not focusable.
      const back = focusBeforeShortcuts
      const usable = back && !(back instanceof HTMLIFrameElement) && dialog.value?.contains(back)
      ;(usable ? back : dialog.value)?.focus()
    }
    focusBeforeShortcuts = null
  }

  function toggleShortcuts() {
    if (shortcutsOpen.value) closeShortcuts(true)
    else openShortcuts()
  }

  // ── feedback ────────────────────────────────────────────────────────────────

  let flashTimer = 0
  let flashId = 0

  function showFlash(shown: Omit<KeyFlash, "id">, spoken: string) {
    flashId += 1
    flash.value = { id: flashId, ...shown }
    announcement.value = spoken
    window.clearTimeout(flashTimer)
    flashTimer = window.setTimeout(() => (flash.value = null), FLASH_MS)
  }

  function togglePlayByKey() {
    player.togglePlay()
    const playing = player.playing.value
    showFlash({ icon: playing ? "mdi-play" : "mdi-pause" }, playing ? t("help.flash.playing") : t("help.flash.paused"))
  }

  function stepSpeedByKey(direction: 1 | -1) {
    const label = formatSpeed(player.stepSpeed(direction))
    showFlash({ label }, t("help.player.speed", { speed: label }))
  }

  // ── the map ─────────────────────────────────────────────────────────────────

  /**
   * What a key does in the player, or null when it is not one of the player's keys. One map for both
   * places a key can land — the dialog and the frame — so the two can never disagree.
   *
   * Read by `event.key`, the character, not by the physical key: "<", ">" and "?" sit in different
   * places on a German and a US keyboard, and each layout's own key for them is the one that works.
   */
  function shortcutFor(event: KeyboardEvent, target: EventTarget | null): (() => void) | null {
    // The browser's and the system's combinations stay theirs: ⌘+← is back, Ctrl+R a reload. AltGr is
    // let through: Windows reports it as Ctrl+Alt, and some layouts need it to type "<" or "?" at all.
    const altGraph = event.getModifierState?.("AltGraph") ?? false
    if ((event.ctrlKey || event.metaKey || event.altKey) && !altGraph) return null
    const { key } = event

    if (key === "?") return toggleShortcuts
    if (key === "Escape") return shortcutsOpen.value ? () => closeShortcuts(true) : () => player.close("escape")
    // While the list is up the player holds still, and its own two keys above are all that work.
    if (shortcutsOpen.value) return null

    if (key === "ArrowRight") return event.shiftKey ? () => player.stepSection(1) : () => void player.next()
    if (key === "ArrowLeft") return event.shiftKey ? () => player.stepSection(-1) : () => void player.previous()
    if (key === "PageDown") return () => player.stepSection(1)
    if (key === "PageUp") return () => player.stepSection(-1)
    if (key === "Home") return () => player.seekEdge("first")
    if (key === "End") return () => player.seekEdge("last")
    if (key === "<") return () => stepSpeedByKey(-1)
    if (key === ">") return () => stepSpeedByKey(1)
    if ((key === " " || key === "Spacebar") && !spaceBelongsTo(target)) return togglePlayByKey
    if (/^[1-9]$/.test(key)) {
      const section = player.sections.value[Number(key) - 1]
      return section ? () => player.seekSection(section) : null
    }
    return null
  }

  /** Set while a Space the player took is still held down — see `onKeyup`. */
  let spaceTaken = false

  /**
   * Keys anywhere on the page while the dialog is up. Controls with keys of their own stop them first.
   *
   * On `document`, bubbling, and **every** key stops here: the dialog is modal, and the host page's own
   * window listeners sit one step further out. Preventing the default alone let them run too — Escape
   * collapsed an enlarged planning section behind the dialog before closing it, and Ctrl+K opened the
   * command palette underneath the backdrop, where it took focus unseen. Stopping propagation leaves
   * the browser's own combinations (Ctrl+R, ⌘+←) alone; those are default actions, not listeners.
   */
  function onKeydown(event: KeyboardEvent) {
    event.stopPropagation()
    const run = shortcutFor(event, event.target)
    if (!run) return
    event.preventDefault()
    if (event.key === " " || event.key === "Spacebar") spaceTaken = true
    run()
  }

  /**
   * A button is pressed by Space on key*up*. Preventing the keydown is what cancels that, but not in
   * every engine — so a Space the player took has its keyup cancelled as well, and a focused button
   * cannot press itself after the player has already acted on the key.
   */
  function onKeyup(event: KeyboardEvent) {
    if (!spaceTaken || (event.key !== " " && event.key !== "Spacebar")) return
    spaceTaken = false
    event.preventDefault()
  }

  /**
   * Keys that land inside the frame.
   *
   * The pointer's actions can move focus into the frame — a Vuetify field focuses itself when pressed,
   * an autocomplete after an option is chosen — and pulling it back out would close the very menu a
   * step is showing. So the player listens there too. The reader cannot use the frame (it is
   * `pointer-events: none`), so a key that lands in it was meant for the player: its keys are handled
   * here and kept away from the app inside, and Tab goes back to the dialog rather than walking through
   * a form nobody can see the focus of.
   */
  function onFrameKeydown(event: KeyboardEvent) {
    // Only the reader's own keystrokes. The walkthrough presses keys inside the frame itself — a scene
    // closes a menu with Escape, and leaving one presses Escape until no overlay is left — and those
    // events come through here too. Forwarded, the walkthrough's own Escape closed the walkthrough, and
    // swallowed, it never reached the menu it was meant to close. The browser marks every real
    // keystroke `isTrusted` and every dispatched event not, and a script cannot forge it.
    if (!event.isTrusted) return
    if (event.key === "Tab") {
      event.preventDefault()
      event.stopImmediatePropagation()
      dialog.value?.focus()
      return
    }
    // No target: Space on a control the reader reached by keyboard is that control's, but inside the
    // frame nothing is the reader's control, so Space there is always the player's.
    const run = shortcutFor(event, null)
    if (!run) return
    event.stopImmediatePropagation()
    event.preventDefault()
    run()
  }

  // Capture phase for the three that record how focus arrives: a Tab's keydown has to be seen before the
  // focus it moves, and a press before the focus it gives.
  onMounted(() => {
    window.addEventListener("pointerdown", onPointerDown, true)
    window.addEventListener("keydown", onAnyKeydown, true)
    window.addEventListener("focusin", onFocusIn, true)
    document.addEventListener("keydown", onKeydown)
    window.addEventListener("keyup", onKeyup)
  })
  onBeforeUnmount(() => {
    window.removeEventListener("pointerdown", onPointerDown, true)
    window.removeEventListener("keydown", onAnyKeydown, true)
    window.removeEventListener("focusin", onFocusIn, true)
    document.removeEventListener("keydown", onKeydown)
    window.removeEventListener("keyup", onKeyup)
    window.clearTimeout(flashTimer)
  })

  return { shortcutsOpen, flash, announcement, toggleShortcuts, closeShortcuts, onFrameKeydown }
}
